import { Link, useParams } from "react-router-dom";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import Seo, { stripHtml } from "@/components/Seo";
import { SocialShare } from "@/components/SocialShare";
import { sanitizeHtml } from "@/components/RichTextEditor";
import { renderArticleBody, cleanExcerpt } from "@/lib/articleHtml";
import api from "@/lib/api";

export default function ArticleDetail() {
  const { id } = useParams();
  const { data: a, isLoading, isError } = useQuery({
    queryKey: ["article", id],
    queryFn: () => api.get(`/articles/${id}`).then((r) => r.data),
  });

  useEffect(() => {
    if (!a?.id || !a.published) return;
    const key = `article_viewed_${a.id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    api.post(`/articles/${a.id}/view`).catch(() => {});
  }, [a?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) return <div className="max-w-3xl mx-auto px-6 py-24 text-[#4A5D6B]">Loading…</div>;
  if (isError || !a) return <div className="max-w-3xl mx-auto px-6 py-24 text-[#4A5D6B]">Article not found. <Link to="/articles" className="text-[#144F75] underline">Back to articles</Link></div>;

  return (
    <article className="pb-24">
      <Seo title={a.title} description={cleanExcerpt(a)} image={a.cover_image} path={`/articles/${a.slug || a.id}`} type="article"
        jsonLd={{ "@context": "https://schema.org", "@type": "Article", headline: a.title,
          description: cleanExcerpt(a), image: a.cover_image || undefined,
          author: { "@type": "Person", name: a.author }, datePublished: (a.created_at || "").slice(0, 10) || undefined,
          publisher: { "@type": "Organization", name: "Enhancing Your World" } }} />
      {a.cover_image && (
        <div className="relative h-[46vh] min-h-[320px]">
          <img src={a.cover_image} alt={a.title} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#144F75]/85 to-[#144F75]/20" />
          <div className="relative max-w-3xl mx-auto px-6 h-full flex flex-col justify-end pb-10">
            <span className="eyw-label !text-[#D98E73] mb-2">{a.category}</span>
            <h1 className="font-serif text-4xl lg:text-6xl text-white leading-tight">{a.title}</h1>
            <p className="text-white/75 text-sm mt-3">By {a.author}</p>
          </div>
        </div>
      )}
      <div className="max-w-3xl mx-auto px-6 pt-12">
        <div className="flex items-center justify-between mb-8">
          <Link to="/articles" className="inline-flex items-center gap-1 text-[#144F75] text-sm hover:gap-2 transition-all"><ArrowLeft size={15} /> All Articles</Link>
          <SocialShare path={`/articles/${a.slug || a.id}`} ogPath={`/api/share/article/${a.slug || a.id}`} title={a.title} testid="article-share" label="Share" />
        </div>
        {!a.cover_image && <h1 className="font-serif text-4xl lg:text-6xl text-[#1A242B] leading-tight mb-6">{a.title}</h1>}
        {a.excerpt && cleanExcerpt(a) && <p className="font-serif text-2xl text-[#4A5D6B] italic leading-relaxed mb-8">{cleanExcerpt(a)}</p>}
        {(() => {
          const r = renderArticleBody(a.body);
          return r.html ? (
            <div className="article-html text-[#1A242B] leading-relaxed text-lg" data-testid="article-body" dangerouslySetInnerHTML={{ __html: r.html }} />
          ) : (
            <div className="text-[#1A242B] leading-relaxed space-y-5 text-lg" data-testid="article-body">
              {r.text.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
            </div>
          );
        })()}
        {a.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-10">
            {a.tags.map((t) => <span key={t} className="rounded-full bg-[#8FA89B]/15 text-[#144F75] text-xs px-3 py-1">#{t}</span>)}
          </div>
        )}
      </div>
    </article>
  );
}
